import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useOutletContext } from 'react-router-dom';
import { ProjectRole } from '@courseai/domain';
import type { ProjectTabContext } from '../ProjectDetail';
import { useT, type TranslationKey } from '../../i18n';
import {
  GET_PROJECT,
  INVITE_USER_TO_PROJECT,
  UPDATE_MEMBER_ROLE,
  REMOVE_MEMBER,
  type ProjectDetailData,
  type GetProjectVariables,
  type InviteMemberData,
  type InviteMemberVariables,
  type UpdateMemberRoleData,
  type UpdateMemberRoleVariables,
  type RemoveMemberData,
  type RemoveMemberVariables,
} from '../../graphql/project-detail';

// Translation keys for the ProjectRole values shown in the members list and
// role selectors.
const ROLE_LABEL_KEYS: Record<ProjectRole, TranslationKey> = {
  owner: 'role.owner',
  editor: 'role.editor',
  viewer: 'role.viewer',
};

const ROLE_OPTIONS: ProjectRole[] = [ProjectRole.owner, ProjectRole.editor, ProjectRole.viewer];

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

// Colaboradores tab: lists the project's members (email + role). Owners get
// management controls — invite by email + role, change roles, and remove
// members (Req 3.1, 3.2). Non-owners see a read-only list. The client never
// sends a userId identity for the caller; the API derives it from the JWT.
export function CollaboratorsTab(): ReactNode {
  const { projectId, canManage } = useOutletContext<ProjectTabContext>();
  const { t } = useT();

  const { data, loading, error, refetch } = useQuery<ProjectDetailData, GetProjectVariables>(
    GET_PROJECT,
    { variables: { projectId } },
  );

  const project = data?.getProject ?? undefined;
  const members = useMemo(() => project?.members ?? [], [project]);
  const ownerCount = useMemo(
    () => members.filter((m) => m.role === ProjectRole.owner).length,
    [members],
  );

  if (loading && data === undefined) {
    return (
      <div className="tab-panel">
        <p>{t('collaborators.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-panel">
        <p className="form-error">{t('collaborators.loadError', { message: error.message })}</p>
        <button type="button" onClick={() => void refetch()}>
          {t('collaborators.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="tab-panel">
      <div className="tab-panel-header">
        <h3>{t('collaborators.title')}</h3>
      </div>

      {canManage && (
        <InviteMemberForm
          projectId={projectId}
          onInvited={async () => {
            await refetch();
          }}
        />
      )}

      {members.length === 0 ? (
        <p className="tab-placeholder">{t('collaborators.empty')}</p>
      ) : (
        <ul className="member-list">
          {members.map((member) => (
            <MemberRow
              key={member.userId}
              projectId={projectId}
              userId={member.userId}
              email={member.email}
              role={member.role}
              canManage={canManage}
              isSoleOwner={member.role === ProjectRole.owner && ownerCount <= 1}
              onChanged={async () => {
                await refetch();
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

interface InviteMemberFormProps {
  projectId: string;
  onInvited(): Promise<void> | void;
}

// Owner-only form to invite a collaborator by email + role. Client-side it only
// requires a non-empty email; the server resolves the account by email and
// rejects unknown emails and duplicates (Req 3.1). No userId identity is sent.
function InviteMemberForm({ projectId, onInvited }: InviteMemberFormProps): ReactNode {
  const { t } = useT();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ProjectRole>(ProjectRole.editor);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  const [inviteUser, { loading }] = useMutation<InviteMemberData, InviteMemberVariables>(
    INVITE_USER_TO_PROJECT,
  );

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitError(undefined);

    const trimmedEmail = email.trim();
    if (trimmedEmail.length === 0) {
      setSubmitError(t('collaborators.invite.emailRequired'));
      return;
    }

    try {
      await inviteUser({
        variables: { input: { projectId, email: trimmedEmail, role } },
      });
      setEmail('');
      setRole(ProjectRole.editor);
      await onInvited();
    } catch (err) {
      setSubmitError(errorMessage(err, t('collaborators.invite.error')));
    }
  }

  return (
    <form className="invite-form" onSubmit={handleSubmit}>
      <label className="invite-form-email">
        {t('collaborators.invite.email')}
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (submitError) {
              setSubmitError(undefined);
            }
          }}
          placeholder={t('collaborators.invite.emailPlaceholder')}
          required
        />
      </label>
      <label className="invite-form-role">
        {t('collaborators.invite.role')}
        <select value={role} onChange={(e) => setRole(e.target.value as ProjectRole)}>
          {ROLE_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {t(ROLE_LABEL_KEYS[r])}
            </option>
          ))}
        </select>
      </label>
      <button type="submit" disabled={loading}>
        {loading ? t('collaborators.invite.inviting') : t('collaborators.invite.invite')}
      </button>
      {submitError && <p className="form-error invite-form-error">{submitError}</p>}
    </form>
  );
}

interface MemberRowProps {
  projectId: string;
  userId: string;
  email: string;
  role: ProjectRole;
  canManage: boolean;
  isSoleOwner: boolean;
  onChanged(): Promise<void> | void;
}

// A single member row. For owners it exposes a role selector and a remove
// button; the sole owner cannot be demoted or removed client-side (the server
// enforces the same last-owner guard, Req 3.8). Non-owners see a read-only row.
function MemberRow({
  projectId,
  userId,
  email,
  role,
  canManage,
  isSoleOwner,
  onChanged,
}: MemberRowProps): ReactNode {
  const { t } = useT();
  const [rowError, setRowError] = useState<string | undefined>(undefined);

  const [updateRole, { loading: updating }] = useMutation<
    UpdateMemberRoleData,
    UpdateMemberRoleVariables
  >(UPDATE_MEMBER_ROLE);
  const [removeMember, { loading: removing }] = useMutation<
    RemoveMemberData,
    RemoveMemberVariables
  >(REMOVE_MEMBER);

  const busy = updating || removing;

  async function handleRoleChange(next: ProjectRole): Promise<void> {
    if (next === role) {
      return;
    }
    setRowError(undefined);
    try {
      await updateRole({ variables: { input: { projectId, userId, role: next } } });
      await onChanged();
    } catch (err) {
      setRowError(errorMessage(err, t('collaborators.roleChangeError')));
    }
  }

  async function handleRemove(): Promise<void> {
    setRowError(undefined);
    try {
      await removeMember({ variables: { input: { projectId, userId } } });
      await onChanged();
    } catch (err) {
      setRowError(errorMessage(err, t('collaborators.removeError')));
    }
  }

  return (
    <li className="member-row">
      <div className="member-row-main">
        <span className="member-row-email">{email}</span>
        {canManage ? (
          <label className="member-row-role-control">
            <span className="visually-hidden">{t('collaborators.role.of', { email })}</span>
            <select
              value={role}
              disabled={busy || isSoleOwner}
              aria-label={t('collaborators.role.of', { email })}
              onChange={(e) => void handleRoleChange(e.target.value as ProjectRole)}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {t(ROLE_LABEL_KEYS[r])}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <span className="member-row-role">{t(ROLE_LABEL_KEYS[role])}</span>
        )}
        {canManage && (
          <button
            type="button"
            className="btn-danger"
            disabled={busy || isSoleOwner}
            title={isSoleOwner ? t('collaborators.soleOwnerTitle') : undefined}
            onClick={() => void handleRemove()}
          >
            {removing ? t('collaborators.removing') : t('collaborators.remove')}
          </button>
        )}
      </div>
      {isSoleOwner && canManage && <p className="field-hint">{t('collaborators.soleOwnerHint')}</p>}
      {rowError && <p className="form-error">{rowError}</p>}
    </li>
  );
}
